import { createClient } from '@/util/supabase/component'
import { referenceQueryFn } from '@/components/upload/queries'
import { queryClient } from '@/util/queryclient'
import type { Reference } from '@/types'

export interface NewReference {
  description: string
  citation: string
}

export class ReferenceService {
  private readonly supabase = createClient()

  async checkExistingReference(
    citation: string,
    description: string,
  ): Promise<number | null> {
    try {
      const existingRef = await queryClient.fetchQuery({
        queryKey: ['reference', citation, description],
        queryFn: referenceQueryFn,
      })
      return existingRef ? existingRef.id : null
    } catch (e) {
      throw new Error(
        `Unable to check for existing reference: ${(e as Error).message}`,
      )
    }
  }

  async createReferences(
    newRefs: NewReference[],
  ): Promise<Record<string, Reference>> {
    if (newRefs.length === 0) {
      return {}
    }

    // Remove duplicates
    const uniqueRefs = Array.from(
      new Set(newRefs.map((ref) => JSON.stringify(ref))),
    ).map((ref) => JSON.parse(ref) as NewReference)

    const res = await this.supabase
      .from('references')
      .insert(uniqueRefs, { count: 'exact' })
      .select()

    if (res.error) {
      throw new Error(`Unable to create references: ${res.error.message}`)
    }

    if (res.status !== 201 || !res.data) {
      throw new Error('Failed to create references')
    }

    // Create mapping from description to reference
    return (res.data as Reference[]).reduce<Record<string, Reference>>(
      (acc, ref) => {
        acc[ref.description] = ref
        return acc
      },
      {},
    )
  }
}
